import { inject, Injectable } from "@angular/core";
import { throwError, type Observable } from "rxjs";
import { map } from "rxjs/operators";

import { AuthServerProvider } from "../auth/auth-jwt.service";

const CALLBACK_KEY = "oauth-handoff:callback";
const REQUEST_ID_KEY = "oauth-handoff:request_id";

@Injectable({ providedIn: "root" })
export class OAuthHandoffService {
    private readonly authServerProvider = inject(AuthServerProvider);

    stashHandoffParams(callback: string, requestId: string | null): void {
        sessionStorage.setItem(CALLBACK_KEY, callback);
        sessionStorage.setItem(REQUEST_ID_KEY, requestId ?? "");
    }

    hasPendingHandoff(): boolean {
        return !!sessionStorage.getItem(CALLBACK_KEY);
    }

    clearPendingHandoff(): void {
        sessionStorage.removeItem(CALLBACK_KEY);
        sessionStorage.removeItem(REQUEST_ID_KEY);
    }

    completeHandoff(): Observable<void> {
        const callback = sessionStorage.getItem(CALLBACK_KEY);
        const requestId = sessionStorage.getItem(REQUEST_ID_KEY) || null;
        sessionStorage.removeItem(CALLBACK_KEY);
        sessionStorage.removeItem(REQUEST_ID_KEY);

        if (!callback) {
            return throwError(() => new Error("No pending OAuth handoff."));
        }

        return this.authServerProvider
            .getHandoffToken(callback, requestId)
            .pipe(
                map((resp) => {
                    this.redirectTo(
                        composeHandoffUrl(
                            callback,
                            resp.handoff_token,
                            requestId,
                        ),
                    );
                }),
            );
    }

    private redirectTo(url: string): void {
        window.location.href = url;
    }
}

export function composeHandoffUrl(
    callback: string,
    token: string,
    requestId: string | null,
): string {
    const url = new URL(callback);
    url.searchParams.set("handoff_token", token);
    if (requestId) {
        url.searchParams.set("request_id", requestId);
    }
    return url.toString();
}
